import { BaseModel } from './base.model';

/**
 * Raw EULA data from API
 */
export interface EulaData {
  version: string;
  content: string;
  valid_from?: string;
  valid_until?: string | null;
}

/**
 * Normalized EULA representation for UI
 */
export interface NormalizedEula {
  version: string;
  content: string;
  validFrom?: string;
  validUntil?: string | null;
}

/**
 * EULA Model with safe getters and validation
 */
export class EulaModel implements BaseModel<NormalizedEula> {
  constructor(public readonly raw: unknown) { }

  private isValidData(data: unknown): data is EulaData {
    if (!data || typeof data !== 'object') return false;

    const eula = data as Partial<EulaData>;

    return (
      typeof eula.version === 'string' &&
      typeof eula.content === 'string' &&
      eula.content.length > 0
    );
  }

  get version(): string {
    if (!this.isValidData(this.raw)) {
      console.error('Invalid EULA data structure:', this.raw);
      return 'unknown';
    }
    return this.raw.version;
  }

  get content(): string {
    if (!this.isValidData(this.raw)) {
      console.error('Invalid EULA data structure:', this.raw);
      return '';
    }
    return this.raw.content;
  }

  get validFrom(): string | undefined {
    if (!this.isValidData(this.raw)) return undefined;
    return this.raw.valid_from;
  }

  get validUntil(): string | null | undefined {
    if (!this.isValidData(this.raw)) return undefined;
    return this.raw.valid_until;
  }

  get isValid(): boolean {
    return this.isValidData(this.raw) && this.content.length > 0;
  }

  toJSON(): NormalizedEula {
    return {
      version: this.version,
      content: this.content,
      validFrom: this.validFrom,
      validUntil: this.validUntil,
    };
  }
}
